/**
 * React Query Hooks for Collaborative Lists - POST/PATCH/DELETE requests
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from '@/lib/apiClient';
import { collaborativeKeys } from '@/api/keys/collaborativeKeys';
import { socialKeys } from '@/api/keys/socialKeys';
import { analytics } from '@/lib/analytics';
import { AnalyticsEvents } from '@/lib/analytics/events';
import { appsFlyerService } from '@/lib/appsflyer';
import {
  CollaborativeListDetails,
  CreateCollaborativeListRequest,
  UpdateCollaborativeListRequest,
  InviteMembersRequest,
  InviteMembersResponse,
  AcceptInvitationRequest,
  AddRestaurantRequest,
  StartVotingRequest,
  CastVoteRequest,
  SubmitRankingsRequest,
  MessageResponse,
  VoteResponse,
  VotingSession,
  CollaborativeRestaurantItem,
} from '@/types/collaborative/collaborativeList';

// ==================== List Management ====================

/**
 * Hook to create a new collaborative list
 */
export function useCreateCollaborativeList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateCollaborativeListRequest) =>
      apiFetch<CollaborativeListDetails>('/collaborative/', {
        method: 'POST',
        body: data,
      }),
    onSuccess: (data, variables) => {
      // Track collaborative list creation
      analytics.capture(AnalyticsEvents.COLLAB_LIST_CREATED, {
        list_id: data.urlKey,
        member_count: variables.invitedUserIds?.length ?? 0,
      });

      // AppsFlyer: Track collaborative list creation
      appsFlyerService.logListCreated('collaborative', false);

      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
    },
  });
}

/**
 * Hook to update a collaborative list
 */
export function useUpdateCollaborativeList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & UpdateCollaborativeListRequest) =>
      apiFetch<CollaborativeListDetails>(`/collaborative/${urlKey}/`, {
        method: 'PATCH',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to delete a collaborative list (creator only)
 */
export function useDeleteCollaborativeList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<void>(`/collaborative/${urlKey}/`, { method: 'DELETE' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
    },
  });
}

// ==================== Member Management ====================

/**
 * Hook to invite members to a collaborative list
 */
export function useInviteMembers() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & InviteMembersRequest) =>
      apiFetch<InviteMembersResponse>(`/collaborative/${urlKey}/invite/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track members invited
      analytics.capture(AnalyticsEvents.COLLAB_MEMBERS_INVITED, {
        list_id: variables.urlKey,
        invite_count: variables.userIds?.length ?? 0,
      });

      // AppsFlyer: Track invite event
      appsFlyerService.logInvite('collaborative_list');

      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to accept an invitation to a collaborative list
 */
export function useAcceptInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & AcceptInvitationRequest) =>
      apiFetch<CollaborativeListDetails>(`/collaborative/${urlKey}/accept/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.restaurants(variables.urlKey) });
    },
  });
}

/**
 * Hook to decline an invitation to a collaborative list
 */
export function useDeclineInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<MessageResponse>(`/collaborative/${urlKey}/decline/`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
      // Also invalidate notifications to update the message
      queryClient.invalidateQueries({ queryKey: socialKeys.notifications() });
    },
  });
}

/**
 * Hook to leave a collaborative list
 */
export function useLeaveCollaborativeList() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<MessageResponse>(`/collaborative/${urlKey}/leave/`, {
        method: 'POST',
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.lists() });
    },
  });
}

/**
 * Hook to remove a member from a collaborative list (creator only)
 */
export function useRemoveMember() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, userId }: { urlKey: string; userId: number }) =>
      apiFetch<MessageResponse>(`/collaborative/${urlKey}/members/${userId}/`, {
        method: 'DELETE',
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}

// ==================== Restaurant Management ====================

/**
 * Hook to add a restaurant to a collaborative list
 */
export function useAddCollaborativeRestaurant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & AddRestaurantRequest) =>
      apiFetch<CollaborativeRestaurantItem>(`/collaborative/${urlKey}/restaurants/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.restaurants(variables.urlKey) });
    },
  });
}

/**
 * Hook to add a restaurant to a collaborative list using Google Place ID
 * This creates the restaurant in the database if it doesn't exist
 */
export function useAddCollaborativeRestaurantWithGoogleId() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, googleId }: { urlKey: string; googleId: string }) =>
      apiFetch<CollaborativeRestaurantItem>(`/collaborative/${urlKey}/add-google-id/`, {
        method: 'PATCH',
        body: { googleId },
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.restaurants(variables.urlKey) });
    },
  });
}

/**
 * Hook to remove a restaurant from a collaborative list
 */
export function useRemoveCollaborativeRestaurant() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, restaurantId }: { urlKey: string; restaurantId: number }) =>
      apiFetch<MessageResponse>(`/collaborative/${urlKey}/restaurants/${restaurantId}/`, {
        method: 'DELETE',
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.restaurants(variables.urlKey) });
    },
  });
}

// ==================== Voting ====================

/**
 * Hook to start a voting session (creator only)
 */
export function useStartVoting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & StartVotingRequest) =>
      apiFetch<VotingSession>(`/collaborative/${urlKey}/voting/start/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track voting started based on mode
      const mode = variables.mode;
      if (mode === 'voting') {
        analytics.capture(AnalyticsEvents.COLLAB_REGULAR_VOTE_STARTED, {
          list_id: variables.urlKey,
        });
      } else if (mode === 'survivor') {
        analytics.capture(AnalyticsEvents.COLLAB_SURVIVOR_VOTE_STARTED, {
          list_id: variables.urlKey,
        });
      } else if (mode === 'ranking') {
        analytics.capture(AnalyticsEvents.COLLAB_RANKING_STARTED, {
          list_id: variables.urlKey,
        });
      }

      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.voting(variables.urlKey) });
    },
  });
}

/**
 * Hook to end a voting session (creator only)
 */
export function useEndVoting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<VotingSession>(`/collaborative/${urlKey}/voting/end/`, {
        method: 'POST',
      }),
    onSuccess: (_, urlKey) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.voting(urlKey) });
    },
  });
}


/**
 * Response from ending a survivor round
 */
export interface RoundEndResult {
  eliminated: number[];
  remaining: number;
  round: number;
  complete: boolean;
  nextRound?: number;
  randomEliminations?: number;
}

/**
 * Hook to manually end the current survivor round.
 * Only available to the list creator in survivor mode.
 * If fewer votes than eliminations needed, fills randomly.
 */
export function useEndCurrentRound() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (urlKey: string) =>
      apiFetch<RoundEndResult>(`/collaborative/${urlKey}/voting/round/end/`, {
        method: 'POST',
      }),
    onSuccess: (_, urlKey) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.voting(urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.votingResults(urlKey) });
    },
  });
}

/**
 * Hook to cast a vote (voting mode)
 */
export function useCastVote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & CastVoteRequest) =>
      apiFetch<VoteResponse>(`/collaborative/${urlKey}/vote/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track vote cast
      analytics.capture(AnalyticsEvents.COLLAB_VOTE_CAST, {
        list_id: variables.urlKey,
        restaurant_id: variables.restaurantId,
        vote_type: 'regular',
      });

      queryClient.invalidateQueries({ queryKey: collaborativeKeys.votingResults(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to cast an elimination vote (survivor mode)
 */
export function useCastEliminationVote() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & CastVoteRequest) =>
      apiFetch<VoteResponse>(`/collaborative/${urlKey}/eliminate/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      // Track elimination vote cast
      analytics.capture(AnalyticsEvents.COLLAB_VOTE_CAST, {
        list_id: variables.urlKey,
        restaurant_id: variables.restaurantId,
        vote_type: 'elimination',
      });

      queryClient.invalidateQueries({ queryKey: collaborativeKeys.votingResults(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}

/**
 * Hook to submit rankings (ranking mode)
 */
export function useSubmitRankings() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ urlKey, ...data }: { urlKey: string } & SubmitRankingsRequest) =>
      apiFetch<MessageResponse>(`/collaborative/${urlKey}/rank/`, {
        method: 'POST',
        body: data,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.votingResults(variables.urlKey) });
      queryClient.invalidateQueries({ queryKey: collaborativeKeys.detail(variables.urlKey) });
    },
  });
}
